import { useCallback, useEffect, useRef } from 'react';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Capacitor } from '@capacitor/core';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { apiVariables } from '../../services/api/apiVariables';
import { getSensitiveData } from '../../utils/secureStorage';
import { useAppSelector } from '../../store';

const isUnauthorizedError = (error: unknown) => {
  const message = String((error as { message?: unknown })?.message ?? error).toLowerCase();
  return message.includes('401') || message.includes('unauthorized');
};

export default function PresenceManager() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const connectionRef = useRef<HubConnection | null>(null);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const rejectedTokenRef = useRef<string | null>(null);

  const disconnect = useCallback(async () => {
    const connection = connectionRef.current;
    connectionRef.current = null;
    isConnectingRef.current = false;

    try {
      await connection?.stop();
    } catch {
      // Connection may already be closed.
    }
  }, []);

  const connect = useCallback(async () => {
    if (!isMountedRef.current || isConnectingRef.current) {
      return;
    }

    const existing = connectionRef.current;
    if (
      existing &&
      existing.state !== HubConnectionState.Disconnected
    ) {
      return;
    }

    isConnectingRef.current = true;

    const authToken = await getSensitiveData('authToken');
    if (!authToken || rejectedTokenRef.current === authToken || !isMountedRef.current) {
      isConnectingRef.current = false;
      return;
    }

    await disconnect();

    if (!isMountedRef.current) {
      isConnectingRef.current = false;
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(apiVariables.CHAT_HUB_URL, {
        accessTokenFactory: async () => (await getSensitiveData('authToken')) ?? '',
        skipNegotiation: Capacitor.isNativePlatform(),
        withCredentials: !Capacitor.isNativePlatform(),
        transport: Capacitor.isNativePlatform()
          ? HttpTransportType.WebSockets
          : HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.None)
      .build();

    connectionRef.current = connection;

    try {
      await connection.start();
      rejectedTokenRef.current = null;
    } catch (error) {
      if (isUnauthorizedError(error)) {
        rejectedTokenRef.current = authToken;
      }

      await disconnect();
    } finally {
      isConnectingRef.current = false;
    }
  }, [disconnect]);

  useEffect(() => {
    isMountedRef.current = true;

    if (isAuthenticated) {
      connect();
    } else {
      rejectedTokenRef.current = null;
      disconnect();
    }

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!isMountedRef.current) {
        return;
      }

      if (user && isAuthenticated) {
        rejectedTokenRef.current = null;
        await connect();
      } else {
        await disconnect();
      }
    });

    return () => {
      isMountedRef.current = false;
      unsubscribe();
      disconnect();
    };
  }, [connect, disconnect, isAuthenticated]);

  return null;
}
